import { lazy, Suspense } from "react";
import { QueryClient } from "@tanstack/react-query";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
} from "react-router-dom";
import { HomePage } from "@/pages/Home";
import { authLoader } from "@/router/loaders/authLoader";
import { cabinetLoader } from "@/router/loaders/cabinetLoader";
import { cabinetPaymentLoader } from "@/router/loaders/cabinetPaymentLoader";
import { indexRedirect } from "@/router/loaders/indexRedirect";
import { mediationLoader } from "@/router/loaders/mediationLoader";
import { refinanceLoader } from "@/router/loaders/refinanceLoader";
import { repeatLoanLoader } from "@/router/loaders/repeatLoanLoader";
import { signupLoader } from "@/router/loaders/signupLoader";
import { RouterUtility } from "@/router/providers/RouterUtility";
import { Layout } from "@/widgets/Layout";

const ErrorRouterPage = lazy(
  () =>
    import(
      /* webpackChunkName: "error-router-page" */ "@/pages/ErrorRouterPage"
    ),
);

export default (queryClient: QueryClient) =>
  createBrowserRouter(
    createRoutesFromElements(
      <Route
        element={<RouterUtility />}
        errorElement={
          <Suspense fallback={null}>
            <ErrorRouterPage />
          </Suspense>
        }
      >
        <Route path="*" loader={indexRedirect} />
        <Route element={<Layout />}>
          <Route path="/" element={<HomePage />} />
          <Route
            path="/auth"
            lazy={() => import(/* webpackPrefetch: true */ "@/pages/Auth")}
            loader={authLoader}
          />
          <Route
            path="/signup"
            lazy={() => import(/* webpackPrefetch: true */ "@/pages/Signup")}
            loader={signupLoader}
          />
          <Route loader={cabinetLoader(queryClient)}>
            <Route
              path="/cabinet"
              lazy={() => import(/* webpackPrefetch: true */ "@/pages/Cabinet")}
            />
            <Route
              path="/cabinet/loan-calculator"
              loader={repeatLoanLoader(queryClient)}
              lazy={() =>
                import(/* webpackPrefetch: true */ "@/pages/LoanCalculator")
              }
            />
            <Route
              path="/cabinet/choose-loan-type"
              loader={repeatLoanLoader(queryClient)}
              lazy={() =>
                import(/* webpackPrefetch: true */ "@/pages/LoanPurpose")
              }
            />
            <Route
              path="/cabinet/loan-payment"
              loader={cabinetPaymentLoader(queryClient)}
              lazy={() =>
                import(/* webpackPrefetch: true */ "@/pages/PaymentSubject")
              }
            />
            <Route
              path="/cabinet/refinance"
              loader={refinanceLoader(queryClient)}
              lazy={() =>
                import(/* webpackPrefetch: true */ "@/pages/Refinance")
              }
            />
            <Route
              path="/verification-result"
              lazy={() =>
                import(/* webpackPrefetch: true */ "@/pages/VerificationResult")
              }
            />
          </Route>
          <Route
            path="/mediation"
            loader={mediationLoader(queryClient)}
            lazy={() => import(/* webpackPrefetch: true */ "@/pages/Mediation")}
          />
          <Route
            path="/payments-info"
            lazy={() =>
              import(/* webpackPrefetch: true */ "@/pages/PaymentsInfo")
            }
          />
          <Route
            path="/loan-conditions"
            lazy={() =>
              import(/* webpackPrefetch: true */ "@/pages/LoanConditions")
            }
          />
          <Route
            path="/payment"
            lazy={() =>
              import(/* webpackPrefetch: true */ "@/pages/PaymentCommon")
            }
          />
          <Route
            path="/gesv-calculator"
            lazy={() =>
              import(/* webpackPrefetch: true */ "@/pages/GESVCalculator")
            }
          />
          <Route path="/feedback" lazy={() => import("@/pages/Feedback")} />
          <Route path="/complaint" lazy={() => import("@/pages/Compliant")} />
          <Route
            path="/how-get-money"
            lazy={() =>
              import(/* webpackPrefetch: true */ "@/pages/HowGetMoney")
            }
          />
          <Route
            path="/faq"
            lazy={() => import(/* webpackPrefetch: true */ "@/pages/FAQ")}
          />
          <Route
            path="/contacts"
            lazy={() => import(/* webpackPrefetch: true */ "@/pages/Contacts")}
          />
          <Route
            path="/debt-disposal"
            lazy={() => import("@/pages/DebtDisposal")}
          />
          <Route
            path="/mock/:date/:iin"
            lazy={() => import("@/pages/BackdoorLogin")}
          />
        </Route>
        <Route path="/statements" lazy={() => import("@/pages/Statement")} />
      </Route>,
    ),
  );
